import React, { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  Button,
  IconButton,
  Divider,
  Chip,
  Grid,
} from "@mui/material";
import {
  CreditCard,
  Payments,
  Stars,
  Close,
  CheckCircle,
} from "@mui/icons-material";
import DialogHeader from "./DialogHeader";
import NumberPad from "./NumberPad";

/**
 * PaymentSplitDialog Component
 * Dialog for splitting payment amounts across multiple payment methods with numpad
 */
const PaymentSplitDialog = ({
  open,
  onClose,
  onConfirm,
  payments,
  setPayments,
  orderTotal,
}) => {
  const theme = useTheme();
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [activeMethod, setActiveMethod] = useState(null);
  const [inputValue, setInputValue] = useState("");

  const paymentConfig = {
    cash: {
      label: "Cash",
      icon: Payments,
      color: theme.palette.success,
    },
    card: {
      label: "Card",
      icon: CreditCard,
      color: theme.palette.info,
    },
    loyalty: {
      label: "Loyalty",
      icon: Stars,
      color: theme.palette.warning,
    },
  };

  const availableMethods = ["cash", "card", "loyalty"];

  // Initialize selected methods from existing payments
  useEffect(() => {
    if (open) {
      if (payments.length > 0) {
        const methods = payments.map((p) => p.method);
        setSelectedMethods(methods);
        if (!activeMethod) {
          setActiveMethod(methods[0]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveMethod(null);
      setInputValue("");
    }
  }, [open]);

  // Calculate totals
  const totalAllocated = payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0,
  );
  const remainingAmount = orderTotal - totalAllocated;

  const handleNumPadClick = (value) => {
    if (value === "⌫") {
      setInputValue((prev) => prev.slice(0, -1));
    } else if (value === ".") {
      if (!inputValue.includes(".")) {
        setInputValue((prev) => prev + value);
      }
    } else {
      setInputValue((prev) => prev + value);
    }
  };

  const handleSetAmount = () => {
    if (!activeMethod || !inputValue) return;

    const amount = Number(inputValue);
    if (isNaN(amount) || amount <= 0) return;

    const existingIndex = payments.findIndex((p) => p.method === activeMethod);

    let newPayments;
    if (existingIndex >= 0) {
      // Update existing payment
      newPayments = [...payments];
      newPayments[existingIndex] = { ...newPayments[existingIndex], amount };
    } else {
      // Add new payment
      newPayments = [...payments, { method: activeMethod, amount }];
    }

    // Auto-fill logic: If only one method left without amount, fill it automatically
    const methodsWithAmounts = newPayments.map((p) => p.method);
    const methodsWithoutAmounts = selectedMethods.filter(
      (m) => !methodsWithAmounts.includes(m),
    );

    if (methodsWithoutAmounts.length === 1) {
      // Calculate remaining amount after current allocation
      const allocatedTotal = newPayments.reduce(
        (sum, p) => sum + Number(p.amount || 0),
        0,
      );
      const remaining = orderTotal - allocatedTotal;

      if (remaining > 0) {
        // Auto-fill the last method
        const lastMethod = methodsWithoutAmounts[0];
        newPayments.push({ method: lastMethod, amount: remaining });
      }
    }

    setPayments(newPayments);
    setInputValue("");
  };

  const handleQuickFill = () => {
    if (!activeMethod || remainingAmount <= 0) return;

    const existingIndex = payments.findIndex((p) => p.method === activeMethod);

    let newPayments;
    if (existingIndex >= 0) {
      newPayments = [...payments];
      newPayments[existingIndex] = {
        ...newPayments[existingIndex],
        amount: remainingAmount,
      };
    } else {
      newPayments = [
        ...payments,
        { method: activeMethod, amount: remainingAmount },
      ];
    }

    // Auto-fill logic: If only one method left without amount, fill it automatically
    const methodsWithAmounts = newPayments.map((p) => p.method);
    const methodsWithoutAmounts = selectedMethods.filter(
      (m) => !methodsWithAmounts.includes(m),
    );

    if (methodsWithoutAmounts.length === 1) {
      // Calculate remaining amount after current allocation
      const allocatedTotal = newPayments.reduce(
        (sum, p) => sum + Number(p.amount || 0),
        0,
      );
      const remaining = orderTotal - allocatedTotal;

      if (remaining > 0) {
        // Auto-fill the last method
        const lastMethod = methodsWithoutAmounts[0];
        newPayments.push({ method: lastMethod, amount: remaining });
      }
    }

    setPayments(newPayments);
  };

  const handleRemovePayment = (method) => {
    setPayments(payments.filter((p) => p.method !== method));
    if (activeMethod === method && selectedMethods.length > 0) {
      setActiveMethod(selectedMethods.find((m) => m !== method) || null);
    }
  };

  const handleToggleMethod = (method) => {
    if (selectedMethods.includes(method)) {
      // Remove method
      setSelectedMethods(selectedMethods.filter((m) => m !== method));
      // Remove its payment if exists
      setPayments(payments.filter((p) => p.method !== method));
      // Update active method if needed
      if (activeMethod === method) {
        const remaining = selectedMethods.filter((m) => m !== method);
        setActiveMethod(remaining.length > 0 ? remaining[0] : null);
      }
    } else {
      // Add method
      const newMethods = [...selectedMethods, method];
      setSelectedMethods(newMethods);
      // Set as active if it's the first one
      if (!activeMethod) {
        setActiveMethod(method);
      }
    }
  };

  const getCurrentAmount = (method) => {
    const payment = payments.find((p) => p.method === method);
    return payment?.amount || 0;
  };

  const isValid = Math.abs(remainingAmount) < 0.01;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogHeader
        icon={<CreditCard sx={{ fontSize: 24 }} />}
        title="Split Payment"
        subtitle={
          selectedMethods.length === 0
            ? "Step 1: Select payment methods"
            : selectedMethods.length === payments.length
              ? "All amounts allocated - Click Confirm"
              : `Step 2: Enter amounts (${payments.length}/${selectedMethods.length} done)`
        }
        onClose={onClose}
      />

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Left Side - Payment Methods */}
          <Grid size={{ xs: 12, md: 5 }}>
            {/* Order Total Summary */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Order Total
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                >
                  Rs {orderTotal.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Allocated
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: isValid
                      ? theme.palette.success.main
                      : theme.palette.text.primary,
                  }}
                >
                  Rs {totalAllocated.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Remaining
                </Typography>
                <Chip
                  label={`Rs ${Math.abs(remainingAmount).toFixed(2)}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: isValid
                      ? alpha(theme.palette.success.main, 0.15)
                      : remainingAmount > 0
                        ? alpha(theme.palette.warning.main, 0.15)
                        : alpha(theme.palette.error.main, 0.15),
                    color: isValid
                      ? theme.palette.success.dark
                      : remainingAmount > 0
                        ? theme.palette.warning.dark
                        : theme.palette.error.dark,
                    border: `1.5px solid ${
                      isValid
                        ? alpha(theme.palette.success.main, 0.4)
                        : remainingAmount > 0
                          ? alpha(theme.palette.warning.main, 0.4)
                          : alpha(theme.palette.error.main, 0.4)
                    }`,
                  }}
                />
              </Box>
            </Paper>

            {/* Method Selection */}
            <Box>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {availableMethods.map((method) => {
                  const config = paymentConfig[method];
                  const Icon = config.icon;
                  const isSelected = selectedMethods.includes(method);

                  return (
                    <Paper
                      key={method}
                      onClick={() => handleToggleMethod(method)}
                      sx={{
                        p: 1.5,
                        flex: 1,
                        minWidth: "90px",
                        cursor: "pointer",
                        borderRadius: 2,
                        border: `3px solid ${
                          isSelected ? config.color.main : "transparent"
                        }`,
                        bgcolor: alpha(theme.palette.grey[100], 0.5),
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: config.color.main,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            bgcolor: isSelected
                              ? alpha(config.color.main, 0.15)
                              : alpha(theme.palette.grey[300], 0.3),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon
                            sx={{
                              color: isSelected
                                ? config.color.main
                                : theme.palette.grey[500],
                              fontSize: 24,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: isSelected ? 700 : 600,
                            color: isSelected
                              ? config.color.main
                              : theme.palette.text.secondary,
                            textAlign: "center",
                          }}
                        >
                          {config.label}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            {/* Payment Method Cards */}
            {selectedMethods.length > 0 && (
              <Box className="my-2">
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {selectedMethods.map((method) => {
                    const config = paymentConfig[method];
                    const Icon = config.icon;
                    const isActive = activeMethod === method;
                    const amount = getCurrentAmount(method);

                    return (
                      <Paper
                        key={method}
                        onClick={() => setActiveMethod(method)}
                        sx={{
                          p: 2.5,
                          cursor: "pointer",
                          borderRadius: 2,
                          border: `3px solid ${
                            isActive ? config.color.main : "transparent"
                          }`,
                          bgcolor: isActive
                            ? alpha(config.color.main, 0.08)
                            : alpha(theme.palette.grey[100], 0.5),
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: config.color.main,
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: alpha(config.color.main, 0.15),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon
                              sx={{ fontSize: 28, color: config.color.main }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 800,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                              }}
                            >
                              {config.label}
                              {isActive && (
                                <Chip
                                  label="ACTIVE"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    fontWeight: 700,
                                    bgcolor: config.color.main,
                                    color: "white",
                                    borderRadius: 1,
                                    height: 20,
                                  }}
                                />
                              )}
                            </Typography>
                            {isActive && inputValue ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    color: config.color.main,
                                  }}
                                >
                                  Rs {inputValue}
                                </Typography>
                                {payments.some(
                                  (p) =>
                                    p.method !== activeMethod && p.amount > 0,
                                ) &&
                                  remainingAmount > 0 && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuickFill();
                                      }}
                                      sx={{
                                        fontSize: "0.7rem",
                                        fontWeight: 700,
                                        borderRadius: 1,
                                        textTransform: "none",
                                        background: `linear-gradient(135deg, ${config.color.main} 0%, ${config.color.dark} 100%)`,
                                        py: 0.5,
                                        px: 1,
                                      }}
                                    >
                                      Fill Rest
                                    </Button>
                                  )}
                              </Box>
                            ) : amount > 0 ? (
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: config.color.main,
                                }}
                              >
                                Rs {amount.toFixed(2)}
                              </Typography>
                            ) : isActive ? (
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: config.color.main,
                                }}
                              >
                                Rs 0
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontWeight: 600,
                                }}
                              >
                                Not allocated
                              </Typography>
                            )}
                          </Box>
                          {amount > 0 && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemovePayment(method);
                              }}
                              sx={{
                                color: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.error.main, 0.2),
                                },
                              }}
                            >
                              <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Grid>

          {/* Right Side - Number Pad */}
          <Grid size={{ xs: 12, md: 7 }}>
            {/* Number Pad */}
            <Box sx={{ mb: 2 }}>
              <NumberPad
                onNumberClick={handleNumPadClick}
                disabled={!activeMethod}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSetAmount}
                disabled={
                  !activeMethod || !inputValue || Number(inputValue) <= 0
                }
                sx={{
                  flex: 1,
                  minHeight: 100,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  background:
                    activeMethod && (!activeMethod || inputValue)
                      ? `linear-gradient(135deg, ${activeMethod ? paymentConfig[activeMethod].color.main : theme.palette.grey[400]} 0%, ${activeMethod ? paymentConfig[activeMethod].color.dark : theme.palette.grey[500]} 100%)`
                      : theme.palette.action.disabledBackground,
                  boxShadow:
                    activeMethod && inputValue && Number(inputValue) > 0
                      ? `0 6px 24px ${alpha(activeMethod ? paymentConfig[activeMethod].color.main : theme.palette.grey[400], 0.4)}`
                      : "none",
                  "&:hover": {
                    boxShadow:
                      activeMethod && inputValue && Number(inputValue) > 0
                        ? `0 8px 32px ${alpha(activeMethod ? paymentConfig[activeMethod].color.main : theme.palette.grey[400], 0.5)}`
                        : "none",
                    transform:
                      activeMethod && inputValue && Number(inputValue) > 0
                        ? "translateY(-2px)"
                        : "none",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CheckCircle sx={{ fontSize: 32 }} />
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Set Amount
                </Typography>
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  if (onConfirm) {
                    onConfirm();
                  } else {
                    onClose();
                  }
                }}
                disabled={!isValid}
                sx={{
                  flex: 1,
                  minHeight: 100,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  background: isValid
                    ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                    : theme.palette.action.disabledBackground,
                  boxShadow: isValid
                    ? `0 6px 24px ${alpha(theme.palette.success.main, 0.4)}`
                    : "none",
                  "&:hover": {
                    boxShadow: isValid
                      ? `0 8px 32px ${alpha(theme.palette.success.main, 0.5)}`
                      : "none",
                    transform: isValid ? "translateY(-2px)" : "none",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CheckCircle sx={{ fontSize: 32 }} />
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Confirm & Place Order
                </Typography>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default PaymentSplitDialog;
